import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { client } from '@/utils/axios/axiosClient';
import { apiUri, routes } from '@/constants/Constants';
import { RentCoverageTypes, SecurityDepositTypes } from '@/constants/CoverageType';
import { UserPermissions } from '@/constants/permissions.constant';
import { checkIfPmi, showSmbInvite } from '@/utils/commonUtils';
import { hasUserPermission, permissionIsNotSet } from '@/utils/permissions.util';
import { useCurrentUser } from '@/stores/useCurrentUser';
import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { Renter, RenterInviteType } from '@/utils/types/renter.interface';
import type { User } from '@/utils/types/User';
import { ScreeningResultsTypes } from './ScreeningResultsTypes';
import { updateRenterInviteType } from './service/RenterInviteAPI';
import { DdClient } from '@/features/DealsDashboard/service/DealsDashboardAPI';
import { buildDdServicePayload } from './utils/buildDdServicePayload';

type SetErrorFn = UseFormSetError<Renter>;

const applyApplicantEmailFieldErrors = (
  errorsData: Record<string, string>,
  setError: SetErrorFn,
) => {
  Object.keys(errorsData).forEach((index) => {
    const msg = errorsData[index];
    if (msg !== '') {
      setError(`applicantEmails.${parseInt(index)}.value`, { message: msg });
    }
  });
};

const applyFaultMessagesToForm = (errorMsgs: Record<string, unknown>, setError: SetErrorFn) => {
  Object.keys(errorMsgs).forEach((key) => {
    const errors = errorMsgs[key];
    if (key === 'applicantEmails' && errors && typeof errors === 'object') {
      applyApplicantEmailFieldErrors(errors as Record<string, string>, setError);
    } else if (typeof errors === 'string' && /applicantEmails/.exec(errors)) {
      const match = /\[(\d+)\]/.exec(errors);
      const index = match?.[1];
      if (index) {
        setError(`applicantEmails.${parseInt(index)}.value`, { message: 'Invalid email.' });
      }
    }
  });
};

// Maps DD service / invite-service field_errors names to React Hook Form paths
const API_TO_FORM_FIELD: Record<string, string> = {
  gross_monthly_rent: 'grossMonthlyRent',
  lease_start: 'leaseStart',
  lease_end: 'leaseEnd',
  prepaid_rent: 'prepaidRent',
  free_rent: 'freeRent',
  unit: 'apartmentNumber',
  screening_status: 'approvalStatus',
};

const mapApiFieldToFormField = (apiField: string): string | null => {
  const applicantMatch = /^invite_recipients\.(\d+)\.email$/.exec(apiField);
  if (applicantMatch) return `applicantEmails.${applicantMatch[1]}.value`;
  if (apiField === 'email') return 'applicantEmails.0.value';

  return API_TO_FORM_FIELD[apiField] ?? null;
};

const getUserType = (user: User) => {
  if (checkIfPmi(user)) return 'PMI';
  return user.LLType;
};

type UseRenterInviteControllerOptions = {
  onSuccess?: (values: Renter) => void;
};

export const useRenterInviteController = (options?: UseRenterInviteControllerOptions) => {
  const [errorMsg, setErrorMsg] = useState<string>('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasTriggeredOnLoadEventsRef = useRef(false);
  const { track } = useAnalyticsContext();

  const { evaluate: evaluateInviteServiceFlag } = useSplitFeatureFlag(
    SplitFeatureFlag.REN_11352_USE_INVITE_SERVICE_FOR_LL_INVITE,
  );

  const {
    treatment: killSwitchNewCoveragesOnRenterInvite,
    isReady: killSwitchNewCoveragesOnRenterInviteIsReady,
  } = useSplitFeatureFlag(SplitFeatureFlag.MONARCH_319_KILL_SWITCH_NEW_COVERAGES_ON_RENTER_INVITE);

  const { treatment: newCoveragesABTestEnabled, isReady: newCoveragesABTestEnabledIsReady } =
    useSplitFeatureFlag(SplitFeatureFlag.MONARCH_319_NEW_COVERAGES_ON_LANDLORD_INVITE);

  const { user } = useCurrentUser();

  const isSmbRenterInvites = useMemo(() => (user ? showSmbInvite(user) : false), [user]);

  const useNewForm = useMemo(() => {
    if (killSwitchNewCoveragesOnRenterInvite === 'off') return false;
    if (!user || checkIfPmi(user) || !isSmbRenterInvites) return false;
    if (user.renterInviteType) {
      return user.renterInviteType === RenterInviteType.SMBInviteNewLimitedSdr;
    }
    return newCoveragesABTestEnabled === 'on';
  }, [killSwitchNewCoveragesOnRenterInvite, newCoveragesABTestEnabled, user, isSmbRenterInvites]);

  useEffect(() => {
    const shouldTriggerFirstLoadEvents =
      newCoveragesABTestEnabledIsReady &&
      killSwitchNewCoveragesOnRenterInviteIsReady &&
      killSwitchNewCoveragesOnRenterInvite === 'on' &&
      isSmbRenterInvites &&
      !hasTriggeredOnLoadEventsRef.current &&
      !!user;

    if (!shouldTriggerFirstLoadEvents) return;

    hasTriggeredOnLoadEventsRef.current = true;

    const renterInviteType = useNewForm
      ? RenterInviteType.SMBInviteNewLimitedSdr
      : RenterInviteType.SMBInviteOldCustomCoverage;

    track('Renter Invite form Visited', {
      newAbTests: {
        variant: renterInviteType,
        experiment: SplitFeatureFlag.MONARCH_319_NEW_COVERAGES_ON_LANDLORD_INVITE,
      },
      landlordType: getUserType(user),
      referrer: window.location.pathname,
    });

    if (user.renterInviteType === null) updateRenterInviteType(queryClient, renterInviteType);
  }, [
    killSwitchNewCoveragesOnRenterInviteIsReady,
    useNewForm,
    track,
    user,
    newCoveragesABTestEnabledIsReady,
    killSwitchNewCoveragesOnRenterInvite,
    isSmbRenterInvites,
    queryClient,
  ]);

  const customCoverageDisabled = useMemo(() => {
    if (!user) return false;

    const isPermissionNotSet = permissionIsNotSet(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
    if (isPermissionNotSet) return false;

    return !hasUserPermission(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
  }, [user]);

  const onSubmit = useCallback(
    async (values: Renter, setError: UseFormSetError<Renter>) => {
      setErrorMsg('');

      const state = values.address.state?.trim().toUpperCase();
      const { isFeatureFlagEnabled } = evaluateInviteServiceFlag(state ? { state } : undefined);
      const useInviteService = !isSmbRenterInvites && isFeatureFlagEnabled;

      let requestPayload: Record<string, unknown>;

      if (useInviteService) {
        requestPayload = buildDdServicePayload(values);
      } else {
        if (useNewForm) {
          values.rentCoverage = RentCoverageTypes.Customize;
          values.securityDepositCoverage = SecurityDepositTypes.CustomizeOrRemove;
          values.renterInviteType = RenterInviteType.SMBInviteNewLimitedSdr;
          values.customCoverage = true;
          if (values.monthsOfCoverage && values.includeDepositCoverage) {
            values.monthsOfCoverage = Math.max(0, values.monthsOfCoverage - 1);
          }
        } else {
          if (user && !checkIfPmi(user) && isSmbRenterInvites) {
            values.renterInviteType = RenterInviteType.SMBInviteOldCustomCoverage;
          }
          if (values.rentCoverage === RentCoverageTypes.CompanyDefault)
            delete values.monthsOfCoverage;
          if (values.securityDepositCoverage === SecurityDepositTypes.CompanyDefault)
            delete values.securityDepositReplacementAmount;
        }
        if (values.customCoverage) {
          values.approvalStatus = ScreeningResultsTypes.Denied;
        }

        const { address, ...rest } = values;
        requestPayload = {
          ...rest,
          building: { monolith_building_ids: [address.monolith_building_id] },
        };
      }

      const requestClient = useInviteService ? DdClient : client;
      const requestUri = useInviteService ? apiUri.inviteViaDealsService : apiUri.inviteRenter;

      try {
        await requestClient
          .post(requestUri, requestPayload, { withCredentials: true })
          .then(() => {
            if (options?.onSuccess) {
              options.onSuccess(values);
            } else {
              navigate(routes.renterInviteConfirmation);
            }
          })
          .catch(
            (
              reason: AxiosError<{
                message?: string;
                field_errors?: Array<{ field: string; message: string }>;
                fault?: { messages?: object };
              }>,
            ) => {
              const data = reason.response?.data;
              if (useInviteService) {
                if (data?.field_errors?.length) {
                  data.field_errors.forEach(({ field, message }) => {
                    const formField = mapApiFieldToFormField(field);
                    if (formField) {
                      setError(formField as Parameters<typeof setError>[0], { message });
                    }
                  });
                  setErrorMsg('Please correct the errors above.');
                  return;
                }
                setErrorMsg(data?.message || 'An error occurred while sending the invite.');
                return;
              }
              const faultMessages = data?.fault?.messages;
              if (faultMessages && typeof faultMessages === 'object') {
                applyFaultMessagesToForm(faultMessages as Record<string, unknown>, setError);
                setErrorMsg('Please correct the errors above.');
                return;
              }
              setErrorMsg(reason.message);
            },
          );
      } catch (_e) {
        setErrorMsg('Error while inviting renter');
      }
    },
    [useNewForm, evaluateInviteServiceFlag, user, isSmbRenterInvites, navigate, options],
  );

  return {
    user,
    errorMsg,
    isSmbRenterInvites,
    useNewForm,
    customCoverageDisabled,
    onSubmit,
  };
};
